import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  catchError,
  EMPTY,
  Observable,
  skip,
  Subject,
  switchMap,
  take,
  takeWhile,
  tap,
  timer,
} from 'rxjs';
import { environment } from '../../environments/environment';
import { JsonResponse } from '../shared/api/backend-config';
import { ARBITRARY_CELL } from '../map/mapbox/services/layer-store.service';
import { ReportTypes } from '../user/user/user.model';
import type { MapboxGeoJSONFeature } from 'mapbox-gl';
import {
  SELECTED_POLYGON,
  SelectedCellService,
} from '../map/mapbox/services/selected-cell.service';
import { MapBoxService } from '../map/mapbox/mapbox.service';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';
import { NavigationService } from '../shared/services/navigation.service';
import { finalize } from 'rxjs/operators';
import { GoogleAnalyticsService } from '../user/google-analytics.service';
import { ReportStatus } from './requested-reports/requested-reports.component';
import { UserAccessService } from '../user/access/user-access.service';

export interface RequestPricesResponse {
  paymentUrl: string;
  totalPriceCents: number;
  reports: {
    reportType: ReportTypes;
    quantity: number;
    priceCents: number;
  }[];
}

export interface RequestPricesBody {
  reports: {
    location: {
      cellId: string | number;
      cellType: string | undefined;
      name: string;
    };
    type: string;
  }[];
}

export interface RequestedReport {
  url?: string;
  name: string;
  orderedAt: string;
  type: string;
  status: string;
  requestId: string;
}

export type ReportsPrices = {
  reportPricesUsd: {
    [key in ReportTypes]: number;
  };
  userAvailableCoupons:
    | {
        [key in ReportTypes]: number;
      }
    | null;
};

export type ReportProcessingStatus = 'idle' | 'processing' | 'ready' | 'error';

export const LAST_UNAUTHORIZED_REPORT_ID = 'LAST_UNAUTHORIZED_REPORT_ID';

const PENDING_MAX_MS = 15 * 60 * 1000;

@Injectable({
  providedIn: 'root',
})
export class ReportsService {
  public lastCreatedCellId: string | null = null;
  public lastOrderedRequestId: string | null = null;

  public pollingSubj = new Subject<void>();

  // Init with default report type
  public lastSelectedReportType: ReportTypes | null =
    ReportTypes.COMMUNITY_COMPASS;

  public pricesData: ReportsPrices | null = null;

  public isSomeReportProcessing: boolean = false;
  // If true do not show latest report link to avoid distracting user with old report
  private firstPoll = true;

  public selectedLocation: string | null = null;
  public selectedRadius: number | null = null;
  public selectedFeature: MapboxGeoJSONFeature | null = null;

  public isLocationSelected = false;

  public isRedirectedToSelectLocation = false;

  public useRadiusOnRedirect = false;

  // Block of flags used for unauthorized reports
  public reportProcessingStatus: ReportProcessingStatus = 'idle';
  public reportLink: string | null = null;
  public unauthorizedReportLink: string | null = null;
  public reportId: string | null = null;
  public isFirstReportAlreadyProcessed: boolean = false;
  public isEmailSubmit: boolean = false;
  public email: string | null = null;
  public showSubmitEmailMessage: boolean = false;

  constructor(
    private http: HttpClient,
    private selectedCellService: SelectedCellService,
    private mapboxService: MapBoxService,
    private accessService: UserAccessService,
    private router: Router,
    private toast: ToastrService,
    private navService: NavigationService,
    private gaService: GoogleAnalyticsService
  ) {
    this.selectedCellService.selectedCellGeoId.subscribe((id) => {
      this.setSelectedReportsFeature(
        this.selectedCellService.getSelectedFeature()
      );
    });

    this.reportId = localStorage.getItem(LAST_UNAUTHORIZED_REPORT_ID);
    if (!!this.reportId) {
      this.getUnauthorizedReportById().subscribe((data) => {
        this.reportProcessingStatus = 'ready';
        this.isFirstReportAlreadyProcessed = true;
        this.unauthorizedReportLink = data.object;

        localStorage.removeItem(LAST_UNAUTHORIZED_REPORT_ID);
      });
    }

    accessService.isUserPlanUpdated
      .pipe(
        skip(2), // Skip initial emissions to get updated data
        take(1)
      )
      .subscribe((upd) => {
        this.startRequestedReportsPolling();
      });
  }

  public getRequestedReports(): Observable<JsonResponse<RequestedReport[]>> {
    return this.http.get<JsonResponse<RequestedReport[]>>(
      `${environment.apiUrl}report/my`
    );
  }

  public requestReport(
    type: 'arbitrary' | 'selected'
  ): Observable<JsonResponse<RequestPricesResponse>> {
    this.isRedirectedToSelectLocation = false;
    this.useRadiusOnRedirect = false;

    const body = this.getRequestPricesBody(type);
    return this.http
      .post<
        JsonResponse<RequestPricesResponse>
      >(`${environment.apiUrl}report/order`, body)
      .pipe(
        tap((res) => {
          const id =
            (res as any)?.object?.requestId ?? (res as any)?.requestId ?? null;

          if (id) {
            this.lastOrderedRequestId = id;

            this.pollingSubj.next();
          }
        })
      );
  }

  public updateReportsPrices(): void {
    if (!this.accessService.isUnauthorized) {
      this.getReportsPrices().subscribe((data) => {
        this.pricesData = data.object;
      });
    }
  }

  public handlePriceResponse(resp: RequestPricesResponse): void {
    if (resp.paymentUrl) {
      window.open(resp.paymentUrl, '_blank');
    }
  }

  public getRequestPricesBody(
    type: 'arbitrary' | 'selected'
  ): RequestPricesBody {
    const isArbitrary = type === 'arbitrary';

    return {
      reports: [
        {
          location: {
            cellId: isArbitrary
              ? this.lastCreatedCellId!
              : this.selectedFeature!.id!,
            cellType: isArbitrary
              ? ARBITRARY_CELL
              : this.selectedCellService.getCellType(),
            name: isArbitrary
              ? this.selectedLocation! + ', ' + this.selectedRadius + ' mile'
              : this.selectedLocation!,
          },
          type: this.lastSelectedReportType!,
        },
      ],
    };
  }

  public getUnauthorizedReportById(): Observable<JsonResponse<any>> {
    return this.http.get<JsonResponse<any>>(
      `${environment.apiUrl}report/free/getReportLinkById?reportId=${this.reportId}`
    );
  }

  public sendUnauthorizedReportToEmail(
    email: string
  ): Observable<JsonResponse<string>> {
    this.reportProcessingStatus = 'processing';
    this.navService.isNavigationBlockedManually = true;
    return this.http
      .get<
        JsonResponse<any>
      >(`${environment.apiUrl}report/free/sendEmail?email=${email}&reportId=${this.reportId}`)
      .pipe(
        finalize(() => {
          this.navService.isNavigationBlockedManually = false;
        }),
        catchError((err) => {
          const errorMessage = err.error.errorMessage;
          if (
            errorMessage === 'Report already sent to this email' ||
            errorMessage === 'Report already sent to another email'
          ) {
            this.toast.warning(
              'Report already sent to email',
              'Please order new report'
            );
          }
          this.reportProcessingStatus = 'error';

          return EMPTY;
        }),
        tap((data) => {
          if (data.object === 'Email sent') {
            this.isEmailSubmit = true;
            this.toast.success('Please check your inbox', 'Email sent');
            this.reportProcessingStatus = 'ready';
          }
        })
      );
  }

  public requestCreateReport(wkt: string): Observable<JsonResponse<string>> {
    return this.http
      .post<JsonResponse<string>>(`${environment.apiUrl}report/free/order`, {
        wktGeometry: wkt,
        locationName: this.selectedLocation,
        type: ReportTypes.COMMUNITY_COMPASS,
      })
      .pipe(
        catchError((err) => {
          const errorMessage = err.error.errorMessage;

          if (errorMessage === 'You can order report only once in 2 minutes') {
            this.toast.warning(
              'You can order report only once in 2 minutes',
              'Please try later'
            );
          } else if (
            errorMessage === 'Limit of 7 reports per email and IP is reached'
          ) {
            this.toast
              .warning(
                'Limit of 7 reports is reached, please sign in to continue',
                'Please sign in'
              )
              .onTap.subscribe(() => this.router.navigate(['sign-in']));
          }
          this.reportProcessingStatus = 'error';
          this.navService.isNavigationBlockedManually = false;

          return EMPTY;
        }),
        tap((data) => {
          this.reportId = data.object;
        })
      );
  }

  public processUnauthorizedReport(wkt: string): void {
    if (!this.isEmailSubmit && this.reportProcessingStatus === 'ready') {
      this.showSubmitEmailMessage = true;
      return;
    }

    this.reportProcessingStatus = 'processing';
    this.navService.isNavigationBlockedManually = true;
    this.gaService.logFreeReportOrder();
    this.requestCreateReport(wkt).subscribe((res) => {
      if (res && res.object) {
        // Start polling every 5 seconds for the report link.
        const pollingSub = timer(0, 5000).subscribe(() => {
          this.getUnauthorizedReportById().subscribe((data) => {
            if (data && data.object) {
              this.unauthorizedReportLink = data.object;
              window.open(data.object, '_blank');

              localStorage.setItem(LAST_UNAUTHORIZED_REPORT_ID, this.reportId!);
              this.isFirstReportAlreadyProcessed = true;

              this.reportProcessingStatus = 'ready';
              this.navService.isNavigationBlockedManually = false;

              if (this.isEmailSubmit && this.email) {
                this.sendUnauthorizedReportToEmail(this.email).subscribe();
              }

              pollingSub.unsubscribe(); // Stop polling once report link is ready.
            }
          });
        });
      }
    });
  }

  public handleReportsDataOnRedirect(source: 'popup' | 'ai' = 'ai'): void {
    // Set to true to handle redirect properly and skip type selection in order-report
    this.isRedirectedToSelectLocation = true;

    // On this step we will determine what report type is selected based on what description we have
    this.setSelectedReportType(ReportTypes.COMMUNITY_COMPASS);

    if (source === 'ai') {
      this.setSelectedReportsFeature(
        this.selectedCellService.getSelectedFeature()
      );
    } else if (source === 'popup') {
      this.useRadiusOnRedirect = true;
    }
  }

  public setSelectedReportsFeature(feature: MapboxGeoJSONFeature | null): void {
    this.selectedFeature = feature;
  }

  public setSelectedReportType(reportType: ReportTypes): void {
    this.lastSelectedReportType = reportType;
  }

  public deselectReportsFeature(): void {
    this.selectedFeature = null;

    if (this.mapboxService.reportsMap.getLayer(SELECTED_POLYGON)) {
      this.mapboxService.reportsMap.removeLayer(SELECTED_POLYGON);
    }
  }

  public startRequestedReportsPolling(): void {
    if (this.accessService.isUnauthorized) return;

    timer(0, 2000)
      .pipe(
        switchMap(() => this.getRequestedReports()),
        tap((data: JsonResponse<RequestedReport[]>) => {
          const reports = data.object ?? [];

          // If we have an active order, wait until /my returns the same requestId
          if (this.lastOrderedRequestId) {
            const tracked = reports.find(
              (r) => r.requestId === this.lastOrderedRequestId
            );

            if (!tracked) {
              // Still waiting for the report with our requestId to appear in /my
              this.isSomeReportProcessing = true;
              this.reportProcessingStatus = 'processing';
              return;
            }

            // Found our report: respect the 15-min pending limit
            if (this.isReportInProgress(tracked)) {
              this.isSomeReportProcessing = true;
              this.reportProcessingStatus = 'processing';
              return;
            }

            if (tracked.status === ReportStatus.READY && tracked.url) {
              this.reportProcessingStatus = 'ready';
              this.reportLink = tracked.url;

              if (!this.firstPoll) {
                window.open(tracked.url, '_blank');
              }

              this.firstPoll = false;
              this.lastOrderedRequestId = null; // stop tracking this requestId
              this.isSomeReportProcessing = false;
              return;
            }

            if (tracked.status === ReportStatus.AUTOMATION_ERROR) {
              this.toast.warning(
                'Something went wrong, please try again later'
              );
              this.reportProcessingStatus = 'error';
              this.reportLink = null;
              this.firstPoll = false;
              this.lastOrderedRequestId = null;
              this.isSomeReportProcessing = false;
              return;
            }

            // Not in progress and not ready/error (e.g. too old pending or NOT_PAID) -> treat as idle and stop tracking
            this.reportProcessingStatus = 'idle';
            this.reportLink = null;
            this.firstPoll = false;
            this.lastOrderedRequestId = null;
            this.isSomeReportProcessing = false;
            return;
          }

          // Determine if any report is in progress
          this.isSomeReportProcessing = reports.some((report) =>
            this.isReportInProgress(report)
          );

          if (this.isSomeReportProcessing) {
            this.reportProcessingStatus = 'processing';
            this.reportLink = null;
          } else {
            const latestReadyReport = reports
              .filter((report) => {
                return (
                  (report.status === ReportStatus.READY && report.url) ||
                  report.status === ReportStatus.AUTOMATION_ERROR
                );
              })
              .sort(
                (a, b) =>
                  new Date(b.orderedAt).getTime() -
                  new Date(a.orderedAt).getTime()
              )[0];

            if (latestReadyReport && latestReadyReport.url && !this.firstPoll) {
              this.reportProcessingStatus = 'ready';
              this.reportLink = latestReadyReport.url;

              window.open(latestReadyReport.url, '_blank');
            } else if (
              latestReadyReport &&
              latestReadyReport.status === ReportStatus.AUTOMATION_ERROR &&
              this.isRecentAutomationError(latestReadyReport) // Also check if report is considered fresh
            ) {
              this.toast.warning(
                'We couldn’t process your latest report. Please try again later.'
              );
              this.reportProcessingStatus = 'error';
              this.reportLink = null;
              this.firstPoll = false;
            } else {
              // No ready reports found
              this.reportProcessingStatus = 'idle';
              this.reportLink = null;
              this.firstPoll = false;
            }
          }
        }),
        // Continue polling while some report is still processing
        // (and also while we are waiting for our tracked requestId to appear)
        takeWhile(
          () => this.isSomeReportProcessing || !!this.lastOrderedRequestId,
          true
        )
      )
      .subscribe({
        error: (err) => {
          console.error('Error during report polling:', err);
          this.reportProcessingStatus = 'error';
          this.isSomeReportProcessing = false; // Stop polling on error
        },
        complete: () => {
          // This will be called when takeWhile condition becomes false
          this.firstPoll = false;
          this.isSomeReportProcessing = false;
        },
      });
  }

  public isReportInProgress(report: RequestedReport): boolean {
    if (!report?.orderedAt) return false;
    const ageMs = Date.now() - new Date(report.orderedAt).getTime();

    const processStatus =
      report.status === ReportStatus.ORDERED ||
      report.status === ReportStatus.IN_PROGRESS_AUTOMATED;
    return processStatus && ageMs <= PENDING_MAX_MS;
  }

  private isRecentAutomationError(
    report: RequestedReport | undefined
  ): boolean {
    if (
      !report ||
      report.status !== ReportStatus.AUTOMATION_ERROR ||
      !report.orderedAt
    )
      return false;
    const ageMs = Date.now() - new Date(report.orderedAt).getTime();
    return ageMs <= PENDING_MAX_MS;
  }

  public outlineReportCell(feature: MapboxGeoJSONFeature): void {
    this.mapboxService.reportsMap.addLayer({
      id: SELECTED_POLYGON,
      type: 'line',
      source: {
        type: 'geojson',
        data: feature,
      },
      paint: {
        'line-color': '#695dff',
        'line-width': 2,
      },
    });
  }

  public resetData(): void {
    this.lastCreatedCellId = null;
    this.pricesData = null;
  }

  private getReportsPrices(): Observable<JsonResponse<ReportsPrices>> {
    return this.http.get<JsonResponse<ReportsPrices>>(
      `${environment.apiUrl}report/prices`
    );
  }
}
